import { Suspense, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Card from "../Card";
import Checkbox from "../Checkbox";
import CollationMode from "./CollationMode";
import ColorPicker from "../ColorPicker";
import { ConditionBuilder } from "../ConditionBuilder";
import { FloatingLabel } from "../FloatingLabel";
import { Col, Row } from "../Grid";
import Select from "../Select";
import TextInput from "../TextInput";
import PropTypes from 'prop-types';

export const validChartSeriesConfig = series => {
    return series?.type && series?.xField && series?.collation;
}

/**
 * A component for editing the configuration of a Chart.js chart series
 * @component
 */
const ChartSeriesConfig = ({ fields, series, onChange, disabled = false }) => {
    const { t } = useTranslation('querybuilder');

    const handleChange = update => {
        onChange(update);
    }

    const options = useMemo(() => [
        {value:"bar", label: t('barChart')},
        {value:"line", label: t('lineChart')},
        {value:"pie", label: t('pieChart')},
        {value:"doughnut", label: t('doughnutChart')},
        {value:"polar", label: t('polarChart')},
        {value:"scatter", label: t('scatterChart')},
        {value:"area", label: t('areaChart')}], [t])

    return <>
        <Card title={t('series')}>
            <Row>
            <Col md="4">
                    <TextInput value={series.label} onChange={label => handleChange({ ...series, label })} placeholder={t('label')} disabled={disabled}></TextInput>
                </Col>
                <Col md="4">
                    <Select value={series.type} onChange={type => handleChange({ ...series, type })} placeholder={t('chartType')} wildcard={false} disabled={disabled} options={options} labelField="label" valueField="value"></Select>
                </Col>
                <Col md="2">
                    <ColorPicker value={series.color} onChange={color => handleChange({ ...series, color })} placeholder={t('color')} disabled={disabled}></ColorPicker>
                </Col>
                <Col md="2">
                    <FloatingLabel>
                        <Checkbox value={series.horizontal} onChange={horizontal => handleChange({ ...series, horizontal })} placeholder={t('horizontal')} disabled={disabled}></Checkbox>
                    </FloatingLabel>
                </Col>
            </Row>
        </Card>
        <Card title={t('filter', { count: 1 })}>
            <Suspense fallback={<Spinner variant="primary"></Spinner>}>
                <ConditionBuilder
                    fields={fields}
                    query={series.query}
                    onQueryChange={query => handleChange({ ...series, query }) }
                    disabled={disabled}
                ></ConditionBuilder>
            </Suspense>
        </Card>
        <Card title={t('select')}>
            <Row>
                <Col>
                    <Select value={series.xField} placeholder={t('independentAxis')} onChange={xField => handleChange({ ...series, xField, mapping: fields.find(f => f.name == xField)?.mapping })} disabled={disabled} options={fields} labelField="label" valueField="name"></Select>
                </Col>
            </Row>
            <Row>
                <Col>
                    <CollationMode collation={series.collation} onChange={collation => handleChange({ ...series, collation: { ...series.collation, ...collation }})} disabled={disabled}></CollationMode>
                </Col>
            </Row>
        </Card>
    </>
}

ChartSeriesConfig.propTypes = {
    /**
     * The series configuration
     */
    series: PropTypes.object,
    /**
     * The data fields avaiable for plotting
     */
    fields: PropTypes.arrayOf(PropTypes.object),
    /**
     * Function to call with an updated series object after an edit
     */
    onChange: PropTypes.func.isRequired,
    /**
     * Force the editor to be read-only
     */
    disabled: PropTypes.bool
}

export default ChartSeriesConfig;